import { getDistanceAlongPath } from '@m1/geo-utils';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useRef, useMemo, useState } from 'react';
import { RiSettings5Line } from 'react-icons/ri';
import { useDispatch, useSelector, useStore } from 'react-redux';

import AddCommandBar from './AddCommandBar';
import Command from './Command/mavlink';
import styles from './index.module.scss';

import actions from '@/actions';
import AddTakeoffModal from '@/components/modals/AddTakeoff';
import EditorBatchModal from '@/components/modals/EditorBatch';
import EditorConfigModal from '@/components/modals/EditorConfig';
import Bar from '@/components/ui/Bar';
import EmptyList from '@/components/ui/EmptyList';
import { FieldNumber } from '@/components/ui/kit/Field';
import MoreMenu from '@/components/ui/MoreMenu';
import { getCommandForSurveySegment } from '@/helpers/CommandConverter';
import useHandlerWithMoveCheck from '@/hooks/useHandlerWithMoveCheck';
import EventEmitter from '@/libs/EventEmitter';
import { modalManager } from '@/libs/ModalContainer';
import { NotifierService as notifier } from '@/libs/Notifier';
import { getDuration } from '@/utils/Formatter';

const cx = classNames.bind(styles);

const Commands = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const moveFromIndex = useSelector((state) => state.editor.moveFromIndex);
  const commands = useSelector((state) => state.mission.json.commands);
  const lastCommandLength = useRef(0);
  const [showSpeedPanel, setShowSpeedPanel] = useState(false);
  const settingsRef = useRef();
  const speedPanelRef = useRef();
  const bodyRef = useRef();
  const speedFieldRef = useRef();
  const [averageSpeed, setAverageSpeed] = useState(10);
  const { handleWithMoveCheck } = useHandlerWithMoveCheck();

  const totalDistance = useMemo(() => {
    const path = [];
    commands.forEach((command) => {
      switch (command.type) {
        case 'navLand':
        case 'navLoiterTime':
        case 'navLoiterToAlt':
        case 'navLoiterTurns':
        case 'navTakeoff':
        case 'navWaypoint':
          path.push(command.data.position);
          break;

        case 'navReturnToLaunch':
          path.push(commands[0].data.position);
          break;

        case 'cusSurvey':
          {
            const surveyCommand = getCommandForSurveySegment(command);
            surveyCommand.data.positions.forEach((position) => path.push(position));
          }
          break;

        default:
          break;
      }
    });

    return getDistanceAlongPath(path);
  }, [commands]);

  const time = useMemo(() => {
    const value = totalDistance / averageSpeed;

    if (Number.isFinite(value) && value > 0) {
      return value * 1000;
    }
    return 0;
  }, [totalDistance, averageSpeed]);

  useUpdateEffect(() => {
    if (lastCommandLength.current < commands.length) {
      bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight });
    }
    lastCommandLength.current = commands.length;
  }, [commands.length]);

  useUpdateEffect(() => {
    if (showSpeedPanel) {
      speedFieldRef.current.focus();
      document.addEventListener('mousedown', unfocusPanel);
    }

    return () => {
      document.removeEventListener('mousedown', unfocusPanel);
    };
  }, [showSpeedPanel]);

  useUpdateEffect(() => {
    if (moveFromIndex === null) return;

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [moveFromIndex]);

  const handleEscKey = (e) => {
    if (e.code === 'Escape') {
      dispatch(actions.editor.setMoveFromIndex(null));
    }
  };

  const unfocusPanel = (e) => {
    if (settingsRef.current.contains(e.target)) return;
    if (speedPanelRef.current.contains(e.target)) return;

    setShowSpeedPanel(false);
  };

  const toggleSpeedPanel = () => {
    setShowSpeedPanel(!showSpeedPanel);
  };

  const changeAverageSpeed = (e) => {
    setAverageSpeed(Number(e.target.value));
  };
  const showSetDefault = () => {
    modalManager.show(EditorConfigModal);
  };

  const showEditAll = () => {
    modalManager.show(EditorBatchModal);
  };

  const toggleAll = (open) => {
    commands.forEach(({ id }) => {
      EventEmitter.publish(`command/${id}/toggle`, open);
    });
  };

  const deleteAll = () => {
    if (store.getState().map3d.isShow) {
      notifier.error('Commands cannot be deleted in 3D map.');
      return;
    }

    modalManager.show(AddTakeoffModal);
    dispatch(actions.mission.resetCommands());
  };

  const moveToHere = (e) => {
    let currentIndex = moveFromIndex;

    let nextIndex = Number(e.currentTarget.dataset.index);
    if (currentIndex < nextIndex) {
      nextIndex -= 1;
    }

    const offset = currentIndex < nextIndex ? 1 : -1;
    while (currentIndex !== nextIndex) {
      dispatch(actions.mission.shiftCommand(currentIndex, offset));
      currentIndex += offset;
    }

    dispatch(actions.editor.setMoveFromIndex(null));
  };

  return (
    <div className={cx('container')}>
      <div className={cx('summary')}>
        <div className={cx('total')}>Total {commaNumber(commands.length)}</div>
        <div className={cx('right')}>
          <div className={cx('item')}>
            {commaNumber((totalDistance / 1000).toFixed(1))}
            <span>km</span>
          </div>
          <Bar />
          <div className={cx('item')}>{getDuration(0, time)}</div>
          <div className={cx('speedSettings')}>
            <div ref={settingsRef} className={cx('settings')} onClick={toggleSpeedPanel}>
              <RiSettings5Line size={14} color="gray" />
            </div>
            <div ref={speedPanelRef} className={cx(['panel', { show: showSpeedPanel }])}>
              <div className={cx('arrow')} />
              <label>
                Average Speed
                <div className={cx('fieldWrapper')}>
                  <FieldNumber
                    ref={speedFieldRef}
                    name="averageSpeed"
                    defaultValue={averageSpeed}
                    onChange={changeAverageSpeed}
                  />
                  <span className={cx('unit')}>m/s</span>
                </div>
              </label>
            </div>
          </div>
          <MoreMenu
            data={[
              { name: 'Set Defaults', callback: showSetDefault },
              { name: 'Edit All', callback: showEditAll },
              { name: 'Open All', callback: () => toggleAll(true) },
              { name: 'Close All', callback: () => toggleAll(false) },
              { name: 'Delete All', callback: handleWithMoveCheck(deleteAll, false) },
            ]}
          />
        </div>
      </div>
      <div ref={bodyRef} className={cx('body')}>
        {commands.length === 0 && (
          <div className={cx('empty')}>
            <EmptyList message="No items" />
          </div>
        )}
        {commands.length > 0 && (
          <div className={cx('inner')}>
            {commands.map((command, index) => {
              const selected = index === moveFromIndex;
              const shouldShowHere = moveFromIndex !== null && ![moveFromIndex - 1, moveFromIndex].includes(index);

              return (
                <div key={command.id} className={cx(['item', { selected }])}>
                  <Command index={index} data={command} />
                  {shouldShowHere && (
                    <div className={cx('here')} data-index={index + 1} onClick={moveToHere}>
                      Move to here
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <AddCommandBar />
    </div>
  );
};

export default Commands;
